/**
 * The survey of the field, as a stored record.
 *
 * A source check asks what the record says about ONE derived claim. Nothing
 * searched the question itself: the seed was refused as "the question, not a
 * claim", so an exploration could run to three hundred claims with its headline
 * never having met a source. A survey is that missing search — and deliberately
 * NOT a check of the seed. A question has no truth value for a page to bear on,
 * so a survey carries no reading, and everything that reads a reading (the
 * `refuted` gate, the taint walk, the canvas hue, a run's stop conditions) has
 * nothing here to read.
 *
 * What it shares with a check is the part that makes a check worth anything: the
 * one citation gate, `matchSeen`, over the URLs the search actually returned.
 *
 * No React, no store, no engine: the codec, the store's undo and the prompt
 * builder all read this, and the codec must not import the store.
 */

import type { FieldSurvey, ReasonNode, SurveyPart } from './types'
import { foldText, textHash } from './similarity'

/** The five questions, in the order a reader meets them. */
export const SURVEY_PARTS: readonly SurveyPart[] = [
  'evidence',
  'landmark',
  'tried',
  'disagreement',
  'untested',
]

/** Their headings. `untested` says "in what came back", because an absence in
 *  one search's results is not an absence in the world. */
export const SURVEY_PART_LABEL: Record<SurveyPart, string> = {
  evidence: 'Main bodies of evidence',
  landmark: 'Landmark results',
  tried: 'What has been tried',
  disagreement: 'Where results disagree',
  untested: 'Not tested in what came back',
}

/** Rows kept per part. Four bodies of evidence, three of everything else: enough
 *  to show the shape of a field, and a bound the prompt can say out loud. */
export const SURVEY_PART_CAP: Record<SurveyPart, number> = {
  evidence: 4,
  landmark: 3,
  tried: 3,
  disagreement: 3,
  untested: 3,
}

/** Distinct sources one survey can rest on. Wider than a check's six, because a
 *  survey spans a field rather than one claim, and bounded for the same reason. */
export const SURVEY_SOURCE_CAP = 8

/** Returned URLs one row may name. */
export const SURVEY_ROW_REFS = 4

/** Character ceilings, shared by admission and the codec so a record read back
 *  off disk is held to the bounds it was written under. */
export const SURVEY_TEXT_CHARS = { summary: 4000, row: 1200, design: 240 } as const

/** Retained queries. `searches` keeps the full count. */
export const SURVEY_QUERY_CAP = 12

/** What needed the key, when a survey is refused for one: the key card's
 *  sentence after the refusal, and the survey press's note before it. */
export const SURVEY_NEED = 'A survey of the field'

/** The durable identity of a question's wording — `claimHashAtCheck`'s rule. */
export const questionHash = (question: string): string => textHash(foldText(question))

/**
 * Whether the live survey still describes the seed above it.
 *
 * The seed has no rewrite door on its card today, but a semantic checkout can
 * put other words there, and a survey of different words is a survey of a
 * different question — so the record says which wording it searched and this
 * compares identities, never timestamps.
 */
export function surveyLive(node: Pick<ReasonNode, 'claim' | 'survey'>): boolean {
  const survey = node.survey
  return !!survey && survey.questionHashAtSurvey === questionHash(node.claim)
}

/** One landing-ordered, duplicate-free stream, by operation id. Array position
 *  is landing order, for `mergeVerificationEvents`'s reason: a wall clock can
 *  move backwards between two real passes. */
export function mergeSurveyEvents(
  ...groups: Array<readonly FieldSurvey[] | undefined>
): FieldSurvey[] {
  const seen = new Set<string>()
  const rows: FieldSurvey[] = []
  for (const group of groups) {
    for (const survey of group ?? []) {
      if (seen.has(survey.opId)) continue
      seen.add(survey.opId)
      rows.push(survey)
    }
  }
  return rows
}

/** Every survey the seed carries, oldest first, the live one last. */
export function surveyEvents(node: Pick<ReasonNode, 'survey' | 'surveyHistory'>): FieldSurvey[] {
  return mergeSurveyEvents(node.surveyHistory, node.survey ? [node.survey] : undefined)
}

/**
 * Install a new survey and move the one it replaces behind it.
 *
 * A re-survey replaces the LIVE record — the prompt block and the card read one
 * survey, the newest — and keeps the earlier one, because it was a paid search
 * that happened and its sources are not recoverable from anything else. Kept on
 * the seed rather than in `verificationArchive`: that archive exists for checks
 * whose claim card left the graph, and the seed never leaves it.
 */
export function appendSurvey(
  node: Pick<ReasonNode, 'survey' | 'surveyHistory'>,
  next: FieldSurvey,
): Pick<ReasonNode, 'survey' | 'surveyHistory'> {
  const earlier = surveyEvents(node).filter((row) => row.opId !== next.opId)
  return {
    survey: next,
    surveyHistory: earlier.length ? earlier : undefined,
  }
}
